import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken } from "../../lib/api";
import { Lock } from "lucide-react";
import { KnovasMark } from "../../components/KnovasLogo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAdminToken(data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="admin-login-page" className="min-h-screen bg-[#2B2B2B] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-baseline mb-10">
          <span className="text-2xl font-black uppercase tracking-[0.04em]" style={{ fontFamily: "'Anton', sans-serif" }}>
            <span style={{ color: "#FF6A00" }}>K</span>
            <span className="text-white">NOVAS</span>
          </span>
          <span className="text-xs tracking-[0.3em] uppercase text-white/60 ml-3 self-center">Admin</span>
        </div>
        <div className="bg-white p-10">
          <div className="flex items-center gap-3 mb-8">
            <Lock size={18} className="text-[#FF6A00]" />
            <h1 className="text-2xl font-black uppercase tracking-tight text-[#2B2B2B]" style={{ fontFamily: "'Anton', sans-serif" }}>Sign In</h1>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Email</label>
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm font-mono"
                placeholder="knovastrading@gmail.com"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Password</label>
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm font-mono"
              />
            </div>
            {error && <p data-testid="admin-login-error" className="text-xs text-red-600">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-[#FF6A00] hover:bg-[#E65C00] disabled:opacity-60 text-white px-6 py-4 text-xs tracking-[0.3em] uppercase font-bold transition-colors"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-xs tracking-[0.3em] uppercase text-white/40 text-center">
          Built Strong · Built to Last
        </p>
      </div>
    </section>
  );
}
